<script setup lang="ts">
import type {
  DungeonChallengeEnvironment,
  DungeonRun,
  ObjectId,
} from '~/composables/charon'

const { charon } = useCharon()
const { t, d } = useI18n()

const props = defineProps<{
  leaderboardSize: number
  challenges: DungeonChallengeEnvironment[]
}>()

const challengePage = ref(0)
const challenge = ref<ObjectId>(props.challenges[0].id)
const challengePlayers = ref(1)

const { data: challengeLeaderboard } = useAsyncData(
  () => charon<DungeonRun[]>(
    `/dungeons/leaderboards/challenge-environments/${challenge.value}`,
    {
      params: {
        'players[size]': challengePlayers.value,
        'sort': ['finishedLevel[dec]', 'duration[asc]'],
        'page': challengePage.value,
        'size': props.leaderboardSize,
      },
    },
  ),
  {
    watch: [challengePlayers, challenge, challengePage],
    lazy: false,
    server: true,
    transform: vs => vs?.map((v, i) => ({ ...v, place: i + 1 + challengePage.value * props.leaderboardSize })) ?? null,
  },
)

const { data: challengeMaxPage } = useAsyncData(
  () => charon<number>(
    `/dungeons/leaderboards/challenge-environments/${challenge.value}/count`,
    {
      params: {
        'players[size]': challengePlayers.value,
      },
    },
  ),
  {
    watch: [challengePlayers, challenge],
    transform: count => Math.ceil((count ?? 0) / props.leaderboardSize),
    lazy: false,
    server: true,
  },
)

// reset page on filter update
watch([challengePlayers, challenge], () => challengePage.value = 0)
</script>

<template>
  <DungeonRunLeaderboard
    v-model:page="challengePage"
    :runs="challengeLeaderboard"
    :max-page="challengeMaxPage ?? 0"
    :size="leaderboardSize"
  >
    <div class="flex flex-col tablet:flex-row gap-2 justify-between">
      <select
        v-model="challenge"
        class="bg-white text-slate-900 px-4 py-2 shadow-lg border border-gray-200 rounded-md hover:bg-slate-100 w-full tablet:w-auto"
      >
        <template
          v-for="entry in props.challenges"
          :key="entry.id"
        >
          <option
            :value="entry.id"
          >
            {{ d(entry.from, 'short') }} – {{ d(entry.until, 'short') }}
          </option>
        </template>
      </select>
      <select
        v-model="challengePlayers"
        class="bg-white text-slate-900 px-4 py-2 shadow-lg border border-gray-200 rounded-md hover:bg-slate-100 w-full tablet:w-auto"
      >
        <template
          v-for="player in [1, 2, 3, 4]"
          :key="player"
        >
          <option
            :value="player"
          >
            {{ t(`jc.view.dungeon.leaderboard.players.label`, { players: player }) }}
          </option>
        </template>
      </select>
    </div>
  </DungeonRunLeaderboard>
</template>
